import type { ComponentType } from 'react';
import { useLingui } from '@lingui/react/macro';
import BigCursorIcon from '../../icons/big-cursor.svg?react';
import BlueYellowIcon from '../../icons/blue-yellow.svg?react';
import ColorBlindnessIcon from '../../icons/color-blindness.svg?react';
import DefaultColorsIcon from '../../icons/default-colors.svg?react';
import DyslexiaFriendlyFontIcon from '../../icons/dyslexia-friendly-font.svg?react';
import EasyReadFontIcon from '../../icons/easy-read-font.svg?react';
import ExtraTextSpacingIcon from '../../icons/extra-text-spacing.svg?react';
import GrayscaleIcon from '../../icons/grayscale.svg?react';
import GreenRedIcon from '../../icons/green-red.svg?react';
import HighContrastIcon from '../../icons/high-contrast.svg?react';
import HighlightLinksIcon from '../../icons/highlight-links.svg?react';
import InvertedColorsIcon from '../../icons/inverted-colors.svg?react';
import KeyboardFocusIcon from '../../icons/keyboard-focus.svg?react';
import LargeClickableAreaIcon from '../../icons/large-clickable-area.svg?react';
import LargeTextIcon from '../../icons/large-text.svg?react';
import ReadingLineIcon from '../../icons/reading-line.svg?react';
import ReadingMaskIcon from '../../icons/reading-mask.svg?react';
import RedGreenIcon from '../../icons/red-green.svg?react';
import StopAnimationIcon from '../../icons/stop-animation.svg?react';
import type {
  ColorAndContrastSetting,
  NavigationSetting,
  TextAndReadabilitySetting
} from '../../types';

export type SettingsData = {
  label: string;
  icon: ComponentType;
};

export const useSettingsData = () => {
  const { t } = useLingui();

  const textAndReadabilitySettings: Record<
    TextAndReadabilitySetting,
    SettingsData
  > = {
    largeText: {
      label: t`Large Text`,
      icon: LargeTextIcon
    },
    extraTextSpacing: {
      label: t`Extra Text Spacing`,
      icon: ExtraTextSpacingIcon
    },
    easyReadFont: {
      label: t`Easy-Read Font`,
      icon: EasyReadFontIcon
    },
    dyslexiaFriendlyFont: {
      label: t`Dyslexia-Friendly Font`,
      icon: DyslexiaFriendlyFontIcon
    },
    readingMask: {
      label: t`Reading Mask`,
      icon: ReadingMaskIcon
    },
    readingLine: {
      label: t`Reading Line`,
      icon: ReadingLineIcon
    },
    stopAnimation: {
      label: t`Stop Animation`,
      icon: StopAnimationIcon
    }
  };

  const navigationSettings: Record<NavigationSetting, SettingsData> = {
    keyboardFocus: {
      label: t`Keyboard Focus`,
      icon: KeyboardFocusIcon
    },
    largeClickableAreas: {
      label: t`Large Clickable Areas`,
      icon: LargeClickableAreaIcon
    },
    bigCursor: {
      label: t`Big Cursor`,
      icon: BigCursorIcon
    },
    highlightLinks: {
      label: t`Highlight Links`,
      icon: HighlightLinksIcon
    }
  };

  const colorAndContrastSettings: Record<
    ColorAndContrastSetting,
    SettingsData
  > = {
    'default-colors': {
      label: t`Default Colors`,
      icon: DefaultColorsIcon
    },
    'high-contrast': {
      label: t`High Contrast`,
      icon: HighContrastIcon
    },
    grayscale: {
      label: t`Grayscale`,
      icon: GrayscaleIcon
    },
    'inverted-colors': {
      label: t`Inverted Colors`,
      icon: InvertedColorsIcon
    },
    'color-blind': {
      label: t`Color Blind`,
      icon: ColorBlindnessIcon
    },
    protanopia: {
      label: t`Red-Green (Protanopia)`,
      icon: RedGreenIcon
    },
    deuteranopia: {
      label: t`Green-Red (Deuteranopia)`,
      icon: GreenRedIcon
    },
    tritanopia: {
      label: t`Blue-Yellow (Tritanopia)`,
      icon: BlueYellowIcon
    }
  };

  return {
    textAndReadabilitySettings,
    navigationSettings,
    colorAndContrastSettings
  };
};
